import { useParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import MarketingLayout from '../components/MarketingLayout';
import SEO from '../components/SEO';
import { blogPosts } from '../data/blogPosts';
import { ArrowLeft, Clock, Tag, ArrowRight } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

const fadeUp = { initial: { opacity: 0, y: 24 }, animate: { opacity: 1, y: 0 } };

export default function BlogPostPage() {
  const { slug } = useParams();
  const post = blogPosts.find((p) => p.slug === slug);

  if (!post) return <Navigate to="/blog" replace />;

  const otherPosts = blogPosts.filter((p) => p.slug !== slug).slice(0, 3);

  return (
    <MarketingLayout>
      <SEO
        title={post.title}
        description={post.excerpt}
        path={`/blog/${post.slug}`}
        keywords={`${post.category}, public access file, H-1B compliance, PAF management`}
      />

      {/* Header */}
      <section className="pt-32 pb-12 lg:pt-40 lg:pb-16 bg-gradient-to-br from-indigo-50/80 via-white to-sky-50/50">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <motion.div {...fadeUp} transition={{ duration: 0.5 }}>
            <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm text-indigo-600 hover:text-indigo-700 font-medium mb-6 group">
              <ArrowLeft className="w-3.5 h-3.5 group-hover:-translate-x-0.5 transition-transform" /> Back to Blog
            </Link>
            <div className="flex items-center gap-3 mb-4">
              <span className="flex items-center gap-1 px-3 py-1 bg-indigo-100 text-indigo-700 text-xs font-semibold rounded-full">
                <Tag className="w-3 h-3" /> {post.category}
              </span>
              <span className="flex items-center gap-1 text-sm text-[#94A3B8]">
                <Clock className="w-3.5 h-3.5" /> {post.readTime}
              </span>
            </div>
            <h1 data-testid="blog-post-title" className="text-3xl lg:text-4xl font-bold text-[#0F172A] tracking-tight mb-4 leading-[1.2]">
              {post.title}
            </h1>
            <p className="text-[#64748B] text-base">{post.date}</p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 lg:py-16 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <motion.article
            {...fadeUp}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="prose prose-slate prose-headings:text-[#0F172A] prose-headings:font-bold prose-p:text-[#475569] prose-p:leading-[1.8] prose-li:text-[#475569] prose-strong:text-[#0F172A] prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4 prose-h3:text-lg prose-h3:mt-8 prose-h3:mb-3 max-w-none"
          >
            <ReactMarkdown>{post.content}</ReactMarkdown>
          </motion.article>
        </div>
      </section>

      {/* Related Posts */}
      {otherPosts.length > 0 && (
        <section className="py-16 lg:py-20 bg-[#F8FAFF]">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-[#0F172A] tracking-tight mb-8">Continue Reading</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {otherPosts.map((p) => (
                <Link key={p.slug} to={`/blog/${p.slug}`} className="group block">
                  <div className="bg-white rounded-2xl border border-indigo-100/50 p-6 hover:shadow-md hover:shadow-indigo-100/40 transition-all h-full flex flex-col">
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-indigo-50 text-indigo-600 text-xs font-semibold rounded-full w-fit mb-3">
                      <Tag className="w-3 h-3" /> {p.category}
                    </span>
                    <h3 className="text-base font-bold text-[#0F172A] mb-2 group-hover:text-indigo-600 transition-colors leading-snug flex-1">
                      {p.title}
                    </h3>
                    <div className="flex items-center gap-1 text-xs text-[#94A3B8] pt-3 border-t border-slate-100">
                      <Clock className="w-3 h-3" /> {p.readTime}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-16 lg:py-20 bg-gradient-to-br from-[#0F172A] to-[#1E3A8A] relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '32px 32px' }} />
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-2xl lg:text-3xl font-bold text-white tracking-tight mb-3">
            Ready to simplify your PAF compliance?
          </h2>
          <p className="text-indigo-200/70 text-base mb-8">
            Join hundreds of employers managing their Public Access Files with PAFHub.
          </p>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 bg-indigo-500 hover:bg-indigo-400 text-white font-semibold px-8 py-3.5 rounded-xl transition-all shadow-lg shadow-indigo-500/25 text-[15px]"
          >
            Get Started Free <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </MarketingLayout>
  );
}
